import { apiGet, apiPut, apiPost, apiDelete } from './api';

interface GroupData {
  id: number;
  name: string;
  description: string | null;
  maxMembers: number;
  createdAt: string;
  updatedAt: string;
  owner: {
    id: number;
    username: string;
    email: string;
  };
  event: {
    id: number;
    title: string;
    slug: string;
  } | null;
  memberCount: number;
  messageCount: number;
  lastMessageAt: string | null;
  isActive: boolean;
}

interface MessageData {
  id: number;
  message: string;
  sentAt: string;
  user: {
    id: number;
    username: string;
    profilePhoto: string | null;
  };
}

interface GroupsResponse {
  groups: GroupData[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface MessagesResponse {
  group: {
    id: number;
    name: string;
    totalMessages: number;
  };
  messages: MessageData[];
  members: Array<{
    id: number;
    username: string;
  }>;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface AdminGroupDetail {
  id: number;
  name: string;
  description: string | null;
  maxMembers: number;
  currentMembers: number;
  featuredImage: string | null;
  createdAt: string;
  updatedAt: string;
  owner: {
    id: number;
    username: string;
    email: string;
    profilePhoto: string | null;
  };
  event: {
    id: number;
    title: string;
    slug: string;
    eventType: string;
    startDate: string;
    status: string;
  } | null;
  premiumFeatures: {
    extendedSize: boolean;
    premiumExpiresAt: string | null;
  };
  statistics: {
    totalMessages: number;
    activeMembers: number;
    pendingMembers: number;
    lastMessageAt: string | null;
    averageMessagesPerDay: number;
  };
  members: Array<{
    id: number;
    user: {
      id: number;
      username: string;
      email: string;
      profilePhoto: string | null;
      createdAt: string;
    };
    status: 'ACCEPTED' | 'PENDING' | 'REJECTED';
    joinedAt: string | null;
    messageCount: number;
  }>;
  recentActivity: Array<{
    type: 'message_sent' | 'member_joined';
    timestamp: string;
    user: string;
    details: string;
  }>;
}

class AdminGroupService {
  /**
   * Get all groups with pagination and metrics
   */
  async getAllGroups(params: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
  } = {}): Promise<GroupsResponse> {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value) !== '') {
        searchParams.append(key, value.toString());
      }
    });

    return apiGet<GroupsResponse>(`/admin/groups?${searchParams.toString()}`);
  }

  /**
   * Get group messages with optional user filtering
   */
  async getGroupMessages(groupId: number, params: {
    page?: number;
    limit?: number;
    userId?: number;
  } = {}): Promise<MessagesResponse> {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value) !== '') {
        searchParams.append(key, value.toString());
      }
    });

    return apiGet<MessagesResponse>(`/admin/groups/${groupId}/messages?${searchParams.toString()}`);
  }

  /**
   * Get detailed group information
   */
  async getGroupDetail(groupId: number): Promise<AdminGroupDetail> {
    return apiGet<AdminGroupDetail>(`/admin/groups/${groupId}`);
  }

  /**
   * Update group details (admin only)
   */
  async updateGroup(groupId: number, updates: {
    name?: string;
    description?: string | null;
    maxMembers?: number;
    featuredImage?: string | null;
  }): Promise<GroupData> {
    return apiPut<GroupData>(`/admin/groups/${groupId}`, updates);
  }

  /**
   * Update group with file upload (admin only)
   */
  async updateGroupWithFile(groupId: number, formData: FormData): Promise<GroupData> {
    return apiPut<GroupData>(`/admin/groups/${groupId}`, formData);
  }

  /**
   * Transfer group ownership (admin only)
   */
  async transferOwnership(groupId: number, newOwnerId: number): Promise<any> {
    return apiPost<any>(`/admin/groups/${groupId}/transfer-owner`, { newOwnerId });
  }

  /**
   * Delete a group completely
   */
  async deleteGroup(groupId: number): Promise<void> {
    return apiDelete<void>(`/admin/groups/${groupId}`);
  }
}

export const adminGroupService = new AdminGroupService();